import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
    User, Mail, Lock, Calendar, Users, ArrowRight, KeyRound, Sparkles
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ChildRegisterPage = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1); // 1: invite code, 2: details
    const [inviteCode, setInviteCode] = useState('');
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        birthday: '',
        gender: ''
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleVerifyCode = (e) => {
        e.preventDefault();
        setError('');
        
        if (inviteCode.length !== 6 || !/^\d{6}$/.test(inviteCode)) {
            setError('Please enter a valid 6-digit invite code');
            return;
        }
        
        setStep(2);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        if (!formData.birthday) {
            setError('Please enter your birthday');
            return;
        }

        // Validate age (13-17)
        const birthDate = new Date(formData.birthday);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }

        if (age < 13) {
            setError('You must be at least 13 years old to use MoodTrack');
            return;
        }

        if (age >= 18) {
            setError('This registration is for minors aged 13-17. Please register a standard account instead.');
            return;
        }

        setLoading(true);

        try {
            const response = await axios.post(`${BACKEND_URL}/api/family/register-child`, {
                invite_code: inviteCode,
                email: formData.email,
                password: formData.password,
                birthday: formData.birthday,
                gender: formData.gender || null
            });

            // Store token and redirect to dashboard
            localStorage.setItem('token', response.data.access_token);
            navigate('/dashboard');
        } catch (err) {
            setError(err.response?.data?.detail || 'Registration failed. Please check your invite code and try again.');
            if (err.response?.data?.detail?.includes('invite code')) {
                setStep(1);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <div className="noise-overlay" />
            
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="w-full max-w-md"
            >
                <Card className="glass-card">
                    <CardContent className="p-8">
                        {/* Header */}
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-br from-primary/20 to-accent/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <Sparkles className="w-8 h-8 text-primary" />
                            </div>
                            <h1 className="font-heading text-2xl font-semibold text-foreground mb-2">
                                {step === 1 ? 'Enter Invite Code' : 'Complete Registration'}
                            </h1>
                            <p className="text-muted-foreground text-sm">
                                {step === 1 
                                    ? 'Ask your parent for your 6-digit invite code' 
                                    : 'Almost there! Fill in your details'
                                }
                            </p>
                        </div>

                        {/* Step indicator */}
                        <div className="flex items-center justify-center gap-2 mb-6">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 1 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                                1
                            </div>
                            <div className={`w-12 h-1 rounded ${step >= 2 ? 'bg-primary' : 'bg-muted'}`} />
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 2 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                                2
                            </div>
                        </div>

                        {step === 1 ? (
                            /* Step 1: Invite Code */
                            <form onSubmit={handleVerifyCode} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="inviteCode">Invite Code</Label>
                                    <div className="relative">
                                        <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                        <Input
                                            id="inviteCode"
                                            data-testid="child-invite-code"
                                            type="text"
                                            placeholder="123456"
                                            value={inviteCode}
                                            onChange={(e) => setInviteCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                            className="pl-10 h-14 rounded-xl bg-muted/30 border-transparent focus:border-primary/50 text-2xl text-center tracking-widest font-mono"
                                            maxLength={6}
                                            required
                                        />
                                    </div>
                                    <p className="text-xs text-muted-foreground text-center">
                                        Your parent will give you this code after creating your profile
                                    </p>
                                </div>

                                {error && (
                                    <div className="p-3 rounded-xl bg-destructive/10 text-destructive text-sm" data-testid="child-register-error">
                                        {error}
                                    </div>
                                )}

                                <Button
                                    type="submit"
                                    data-testid="child-verify-code-btn"
                                    className="w-full h-12 rounded-full text-lg font-medium"
                                >
                                    Continue
                                    <ArrowRight className="ml-2 w-5 h-5" />
                                </Button>
                            </form>
                        ) : (
                            /* Step 2: Registration Details */
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="p-3 rounded-xl bg-primary/5 border border-primary/20 text-center mb-4">
                                    <p className="text-sm text-muted-foreground">Invite Code: <span className="font-mono font-medium text-foreground">{inviteCode}</span></p>
                                    <button 
                                        type="button"
                                        onClick={() => setStep(1)}
                                        className="text-xs text-primary hover:underline"
                                    >
                                        Change code
                                    </button>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="email">Your Email</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                        <Input
                                            id="email"
                                            data-testid="child-register-email"
                                            type="email"
                                            placeholder="you@example.com"
                                            value={formData.email}
                                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                                            className="pl-10 h-12 rounded-xl bg-muted/30 border-transparent focus:border-primary/50"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="birthday">Birthday</Label>
                                        <div className="relative">
                                            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                            <Input
                                                id="birthday"
                                                data-testid="child-register-birthday"
                                                type="date"
                                                value={formData.birthday}
                                                onChange={(e) => setFormData({...formData, birthday: e.target.value})}
                                                className="pl-10 h-12 rounded-xl bg-muted/30 border-transparent focus:border-primary/50"
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="gender">Gender</Label>
                                        <div className="relative">
                                            <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                            <select
                                                id="gender"
                                                data-testid="child-register-gender"
                                                value={formData.gender}
                                                onChange={(e) => setFormData({...formData, gender: e.target.value})}
                                                className="w-full pl-10 h-12 rounded-xl bg-muted/30 border-transparent focus:border-primary/50 text-foreground appearance-none cursor-pointer"
                                                style={{ backgroundColor: 'hsl(var(--muted) / 0.3)' }}
                                            >
                                                <option value="">Select...</option>
                                                <option value="M">Male</option>
                                                <option value="F">Female</option>
                                                <option value="Prefer not to say">Prefer not to say</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="password">Create Password</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                        <Input
                                            id="password"
                                            data-testid="child-register-password"
                                            type="password"
                                            placeholder="••••••••"
                                            value={formData.password}
                                            onChange={(e) => setFormData({...formData, password: e.target.value})}
                                            className="pl-10 h-12 rounded-xl bg-muted/30 border-transparent focus:border-primary/50"
                                            required
                                            minLength={6}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                        <Input
                                            id="confirmPassword"
                                            data-testid="child-register-confirm-password"
                                            type="password"
                                            placeholder="••••••••"
                                            value={formData.confirmPassword}
                                            onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                                            className="pl-10 h-12 rounded-xl bg-muted/30 border-transparent focus:border-primary/50"
                                            required
                                        />
                                    </div>
                                </div>

                                {error && (
                                    <div className="p-3 rounded-xl bg-destructive/10 text-destructive text-sm" data-testid="child-register-error">
                                        {error}
                                    </div>
                                )}

                                <Button
                                    type="submit"
                                    data-testid="child-register-submit"
                                    className="w-full h-12 rounded-full text-lg font-medium shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                                    disabled={loading}
                                >
                                    {loading ? 'Creating Account...' : 'Join MoodTrack'}
                                    <ArrowRight className="ml-2 w-5 h-5" />
                                </Button>
                            </form>
                        )}

                        {/* Info */}
                        <div className="mt-6 p-4 rounded-xl bg-muted/30 text-center">
                            <p className="text-xs text-muted-foreground">
                                Your account will be linked to your parent&apos;s family plan. 
                                They&apos;ll be able to see your mood summaries to support your wellbeing.
                            </p>
                        </div>

                        {/* Links */}
                        <div className="mt-6 text-center text-sm">
                            <p className="text-muted-foreground">
                                Already have an account?{' '}
                                <Link to="/login" className="text-primary font-medium hover:underline">
                                    Sign in
                                </Link>
                            </p>
                            <p className="text-muted-foreground mt-2">
                                18 or older?{' '}
                                <Link to="/register" className="text-primary font-medium hover:underline">
                                    Create standard account
                                </Link>
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>
        </div>
    );
};

export default ChildRegisterPage;
